// ============================================================================
// CHANGER LES HORAIRES — brouillon, aperçu, application
// ============================================================================
// Retour du boss sur l'aperçu « Ce que ça change » :
//
//   « Ça c'est méga moche, il faut que ça sorte comme sur le reste. »
//   « Dans ce module il faudrait qu'on puisse modifier les horaires directement
//     sur le visuel, vu que ça ne touche que cet onglet. Genre on clique sur la
//     case bleue de 9h-12h30 et on peut la mettre de 9h-12h directement. Une
//     fois qu'on a tout bien choisi on fige et on applique, ou si on quitte ça
//     annule tout. Et si le multi-site est activé, avoir le visuel sur tout le
//     monde et qu'on puisse directement modifier dessus. »
//
// D'où cet écran :
//   - l'aperçu n'est plus un tableau de texte mais le MÊME visuel que le
//     planning et la vue multi-site : semaines empilées, cases bleues coloriées
//     par poste, horaires dedans ;
//   - une case s'édite au clic. On modifie la SEMAINE TYPE — c'est la seule
//     chose que ce module sait appliquer, et c'est ce qui rend l'édition sans
//     risque : rien n'est écrit tant qu'on n'a pas appuyé sur « Appliquer » ;
//   - en multi-site, les officines sont empilées les unes sous les autres et
//     toutes éditables, y compris pour déplacer une journée d'un site à l'autre.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO, startOfISOWeek, getISOWeek, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  FlaskConical, ChevronDown, ChevronRight, Rocket, X, AlertTriangle, CalendarDays,
  Maximize2, Minimize2, Building2, Undo2, Sliders, RefreshCw, Info,
} from 'lucide-react';
import {
  useSimulationDrafts, useSimulationPreview, useApplySimulation, usePharmacySettings,
  useSites, useSitePharmacy,
  type SimDraft, type SimPreview, type SimCell, type SimScope,
} from '@/api/resources';
import { WeeklyTemplateEditor } from '@/components/WeeklyTemplateEditor';
import { Button } from '@/components/ui/Button';
import { positionStyle, byPositionThenName } from '@/lib/positions';
import { slotDefaults, closedByWeekday, WEEK_DAY_LABELS, WEEK_DAY_NUMBERS } from '@/lib/schedule';
import { absenceMeta, ABSENCE_CELL_MUTED } from '@/lib/absenceTypes';
import type { EmployeePosition, ScheduleMode, TemplateWeekType, WeeklyTemplateDay } from '@/types';

/** Format de date affiché sur cette page : jour/mois/année, comme sur les documents. */
const DATE_FMT = 'dd/MM/yyyy';

/** Longueur de l'aperçu : quatre semaines pleines, l'horizon que le boss regarde. */
const PREVIEW_DAYS = 28;

const POSITION_LABELS: Record<EmployeePosition, string> = {
  PHARMACIEN: 'Pharmacien·ne',
  ETUDIANT_REMPLACANT_PHARMACIEN: 'Étudiant remplaçant pharmacien',
  PREPARATEUR: 'Préparateur·trice',
  ETUDIANT: 'Étudiant',
  MAGASINIER: 'Magasinier·e',
  MENAGE: 'Ménage',
  AUTRE: 'Autre',
};

const DAYS_OFF = [
  { v: null as number | null, l: 'Aucun' },
  { v: 1, l: 'Lun' }, { v: 2, l: 'Mar' }, { v: 3, l: 'Mer' },
  { v: 4, l: 'Jeu' }, { v: 5, l: 'Ven' }, { v: 6, l: 'Sam' }, { v: 0, l: 'Dim' },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/** La semaine type applicable à une date : B en semaine ISO impaire si alternance. */
function weekTypeOf(mode: ScheduleMode, date: Date): TemplateWeekType {
  return mode === 'ALTERNATING' && getISOWeek(date) % 2 === 1 ? 'B' : 'A';
}

/**
 * Heures d'une case, pour le total de la ligne.
 *
 * `halves` restreint le compte aux demi-journées faites DANS cette officine :
 * une journée partagée (matin ici, après-midi ailleurs) ne doit pas gonfler le
 * total des deux blocs.
 */
function cellHours(
  c: SimCell | undefined,
  halves: { morning: boolean; afternoon: boolean } = { morning: true, afternoon: true },
): number {
  if (!c || c.status !== 'WORK') return 0;
  const part = (a?: string | null, b?: string | null) => {
    if (!a || !b) return 0;
    const [sh, sm] = a.split(':').map(Number);
    const [eh, em] = b.split(':').map(Number);
    return Math.max(0, eh + em / 60 - (sh + sm / 60));
  };
  return (
    (halves.morning ? part(c.morningStart, c.morningEnd) : 0) +
    (halves.afternoon ? part(c.afternoonStart, c.afternoonEnd) : 0)
  );
}

/** Deux cases sont identiques si statut, motif, officine et horaires coïncident. */
function sameCell(a: SimCell | undefined, b: SimCell | undefined): boolean {
  if (!a || !b) return a === b;
  return (
    a.status === b.status &&
    a.absenceType === b.absenceType &&
    a.siteId === b.siteId &&
    a.afternoonSiteId === b.afternoonSiteId &&
    a.morningStart === b.morningStart &&
    a.morningEnd === b.morningEnd &&
    a.afternoonStart === b.afternoonStart &&
    a.afternoonEnd === b.afternoonEnd
  );
}

/** Le texte d'une case, pour l'infobulle. */
function cellTooltip(c: SimCell | undefined): string {
  if (!c) return 'Repos';
  if (c.status === 'NOT_EMPLOYED') return 'Hors période du contrat';
  if (c.status === 'OFF') return 'Repos';
  if (c.status !== 'WORK') return c.absenceType ? absenceMeta(c.absenceType as never).label : c.status;
  const parts: string[] = [];
  if (c.morningStart) parts.push(`matin ${c.morningStart}-${c.morningEnd}`);
  if (c.afternoonStart) parts.push(`après-midi ${c.afternoonStart}-${c.afternoonEnd}`);
  return parts.join(' · ') || 'Repos';
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function SimulationPage() {
  const { data: sitesData } = useSites();
  const multiSite = !!sitesData?.multiSite;

  // Portée : l'officine active, ou toutes les officines empilées. Le multi-site
  // s'ouvre d'emblée sur le groupe — c'est la demande : « avoir le visuel sur
  // tout le monde ». Le sélecteur n'apparaît qu'à partir de deux officines.
  const [scope, setScope] = useState<SimScope>('site');
  useEffect(() => {
    if (multiSite) setScope('group');
  }, [multiSite]);

  const { data: draftData, isLoading } = useSimulationDrafts(scope);
  const initialDrafts = draftData?.drafts;
  const sites = useMemo(() => draftData?.sites ?? [], [draftData]);

  // Horaires de l'officine active : ils pré-remplissent la semaine type simulée
  // et décident si le dimanche y est proposé.
  const { data: pharmacy } = usePharmacySettings();
  const previewMut = useSimulationPreview();
  const applyMut = useApplySimulation();

  const [drafts, setDrafts] = useState<SimDraft[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [preview, setPreview] = useState<SimPreview | null>(null);
  /** Aperçu calculé avec les brouillons D'ORIGINE — sert de point de comparaison. */
  const [baseline, setBaseline] = useState<SimPreview | null>(null);
  const [mode, setMode] = useState<'permanent' | 'temporary'>('permanent');
  const [startDate, setStartDate] = useState<string>(todayISO());
  const [endDate, setEndDate] = useState<string>('');
  const [keepExceptions, setKeepExceptions] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (initialDrafts) setDrafts(initialDrafts);
  }, [initialDrafts]);

  const toPayload = (d: SimDraft) => ({
    employeeId: d.employeeId,
    scheduleMode: d.scheduleMode,
    fixedDayOff: d.fixedDayOff,
    fixedDayOff2: d.fixedDayOff2,
    rows: d.rows,
  });

  const payloads = useMemo(() => drafts.map(toPayload), [drafts]);

  /**
   * Qui a réellement été touché. Sans ça, `apply` envoyait TOUS les salariés :
   * sans effet en permanent (on réécrit la même semaine type), mais en temporaire
   * ça figeait en MANUAL même les gens qu'on n'avait jamais ouverts.
   */
  const modifiedIds = useMemo(() => {
    if (!initialDrafts) return new Set<string>();
    const initialByEmp = new Map(initialDrafts.map((d) => [d.employeeId, d]));
    const signature = (d: SimDraft) =>
      JSON.stringify({
        scheduleMode: d.scheduleMode,
        fixedDayOff: d.fixedDayOff ?? null,
        fixedDayOff2: d.fixedDayOff2 ?? null,
        rows: [...d.rows]
          .filter((r) => r.works)
          .sort((a, b) => `${a.weekType}${a.dayOfWeek}`.localeCompare(`${b.weekType}${b.dayOfWeek}`))
          .map((r) => [
            r.weekType, r.dayOfWeek,
            r.morningStart, r.morningEnd, r.afternoonStart, r.afternoonEnd,
            // Les officines du jour font partie de la semaine type : déplacer un
            // mercredi d'un site à l'autre — ou n'y déplacer que l'après-midi —
            // est bien une modification.
            r.pharmacyId ?? null,
            r.afternoonPharmacyId ?? null,
          ]),
      });
    const changed = new Set<string>();
    for (const d of drafts) {
      const before = initialByEmp.get(d.employeeId);
      if (before && signature(before) !== signature(d)) changed.add(d.employeeId);
    }
    return changed;
  }, [drafts, initialDrafts]);

  const modifiedDrafts = useMemo(
    () => drafts.filter((d) => modifiedIds.has(d.employeeId)).map(toPayload),
    [drafts, modifiedIds],
  );

  // --- Aperçu recalculé tout seul -----------------------------------------
  // L'ancien écran demandait de cliquer « Voir ce que ça change » après chaque
  // retouche. Puisqu'on édite maintenant SUR l'aperçu, il doit suivre : on
  // recalcule en différé (le temps de finir de taper une heure), et seule la
  // dernière réponse est retenue.
  const requestId = useRef(0);
  const runPreview = useCallback(async () => {
    if (!initialDrafts || drafts.length === 0) return;
    const id = ++requestId.current;
    const common = { startDate, keepExceptions, scope, days: PREVIEW_DAYS };
    const [base, next] = await Promise.all([
      previewMut.mutateAsync({ drafts: initialDrafts.map(toPayload), ...common }),
      previewMut.mutateAsync({ drafts: drafts.map(toPayload), ...common }),
    ]);
    if (id !== requestId.current) return; // une frappe plus récente a pris la main
    setBaseline(base);
    setPreview(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, initialDrafts, startDate, keepExceptions, scope]);

  useEffect(() => {
    if (!initialDrafts || drafts.length === 0) return;
    const t = setTimeout(() => { void runPreview(); }, 400);
    return () => clearTimeout(t);
  }, [runPreview, initialDrafts, drafts.length]);

  // --- Quitter annule tout -------------------------------------------------
  // Les brouillons ne vivent que dans cette page : changer d'écran les perd,
  // c'est voulu. Reste le cas du rechargement / de la fermeture d'onglet, où le
  // navigateur peut prévenir.
  useEffect(() => {
    if (modifiedIds.size === 0) return;
    const onLeave = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [modifiedIds.size]);

  function updateDraft(employeeId: string, patch: Partial<SimDraft>) {
    setDrafts((prev) => prev.map((d) => (d.employeeId === employeeId ? { ...d, ...patch } : d)));
  }

  /**
   * Le cœur de l'édition au clic : modifier UNE journée de semaine type.
   *
   * Une case du planning n'est pas une date isolée — c'est la semaine type qui
   * la produit. Éditer le mercredi 14/10 modifie donc tous les mercredis (de la
   * semaine A ou B selon le cas). C'est dit dans l'éditeur, et c'est la seule
   * chose que « Appliquer » sait enregistrer.
   */
  const editTemplateDay = useCallback(
    (
      employeeId: string,
      weekType: TemplateWeekType,
      dayOfWeek: number,
      patch: Partial<WeeklyTemplateDay> | null,
    ) => {
      setDrafts((prev) =>
        prev.map((d) => {
          if (d.employeeId !== employeeId) return d;
          const idx = d.rows.findIndex((r) => r.weekType === weekType && r.dayOfWeek === dayOfWeek);
          const existing = idx >= 0 ? d.rows[idx] : undefined;
          const base: WeeklyTemplateDay = existing ?? {
            weekType, dayOfWeek, works: false,
            morningStart: null, morningEnd: null, afternoonStart: null, afternoonEnd: null,
            pharmacyId: null, afternoonPharmacyId: null,
          };
          // `null` = repos : on garde la ligne (elle porte encore son officine)
          // mais elle ne travaille plus. `apply` ne conserve que les jours
          // travaillés, donc elle disparaîtra à l'enregistrement.
          const next: WeeklyTemplateDay = patch === null
            ? {
                ...base, works: false,
                morningStart: null, morningEnd: null, afternoonStart: null, afternoonEnd: null,
                // Plus d'après-midi : plus de seconde officine non plus.
                afternoonPharmacyId: null,
              }
            : { ...base, ...patch };
          const rows = idx >= 0
            ? d.rows.map((r, i) => (i === idx ? next : r))
            : [...d.rows, next];
          return { ...d, rows };
        }),
      );
    },
    [],
  );

  /**
   * Lever le repos fixe d'un jour, depuis la case elle-même.
   *
   * Le repos fixe hebdomadaire l'emporte sur la semaine type : tant qu'il est
   * posé, les horaires saisis sur ce jour sont ignorés, en silence. C'est la
   * troisième raison pour laquelle « peu importe ce que je fais ça ne change
   * pas » — on modifiait un jour que le moteur remettait au repos juste après.
   * L'éditeur le dit maintenant, et le règle sur place.
   */
  function clearFixedOff(employeeId: string, weekday: number) {
    setDrafts((prev) =>
      prev.map((d) =>
        d.employeeId === employeeId
          ? {
              ...d,
              fixedDayOff: d.fixedDayOff === weekday ? null : d.fixedDayOff,
              fixedDayOff2: d.fixedDayOff2 === weekday ? null : d.fixedDayOff2,
            }
          : d,
      ),
    );
  }

  function resetAll() {
    if (initialDrafts) setDrafts(initialDrafts);
  }

  async function handleApply() {
    await applyMut.mutateAsync({
      drafts: modifiedDrafts,
      mode,
      startDate,
      endDate: mode === 'temporary' ? endDate : undefined,
      keepExceptions,
      scope,
    });
    setShowApply(false);
  }

  if (isLoading) return <div className="p-6 text-[13px] text-slate-500">Chargement…</div>;

  const boards = preview && (
    <PreviewBoards
      preview={preview}
      baseline={baseline}
      drafts={drafts}
      sites={sites}
      scope={scope}
      sitesMeta={sitesData?.sites ?? []}
      startDate={startDate}
      fullscreen={fullscreen}
      onToggleFullscreen={() => setFullscreen((v) => !v)}
      onEditDay={editTemplateDay}
      onClearFixedOff={clearFixedOff}
      refreshing={previewMut.isPending}
    />
  );

  // Le plein écran prend toute la page : l'aperçu EST l'outil de travail, on ne
  // veut pas y accéder par un scroll sous trois panneaux.
  if (fullscreen && boards) {
    return <div className="fixed inset-0 z-50 bg-white flex flex-col">{boards}</div>;
  }

  return (
    <div className="h-full overflow-y-auto">
    <div className="p-6 pb-24 space-y-5 max-w-[1400px] mx-auto">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1">
            <FlaskConical size={13} /> Test avant application
          </div>
          <h2 className="text-[26px] font-semibold tracking-tight leading-tight">Changer les horaires</h2>
          <p className="text-[12.5px] text-slate-500 mt-1 max-w-2xl">
            Cliquez sur une case de l'emploi du temps pour changer ses horaires, voyez tout de suite
            ce que ça donne sur quatre semaines, puis appliquez.{' '}
            <strong>Rien n'est enregistré tant que vous n'appliquez pas — quitter cette page annule tout.</strong>
          </p>
        </div>

        {multiSite && (
          <div className="flex rounded-lg border border-slate-200 overflow-hidden flex-shrink-0">
            {([['Cette officine', 'site'], ['Toutes les officines', 'group']] as const).map(([label, v]) => (
              <button
                key={v}
                type="button"
                onClick={() => setScope(v)}
                className={`px-3 py-1.5 text-[11.5px] font-medium ${
                  scope === v ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Nature du changement — posée AVANT l'édition : elle conditionne tout le reste. */}
      <PlanBar
        mode={mode}
        setMode={setMode}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />

      {/* L'aperçu éditable : c'est lui l'écran de travail. */}
      {boards}
      {!preview && (
        <div className="border border-slate-200 rounded-xl bg-white p-6 text-center text-[12.5px] text-slate-400">
          Calcul de l'emploi du temps…
        </div>
      )}

      {/* Réglages qu'une grille ne sait pas dire : repos fixes, semaines A/B,
          semaine type complète. Repliés par défaut — l'édition passe d'abord
          par le visuel. */}
      <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <button
          onClick={() => setShowSettings((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 text-left"
        >
          <Sliders size={14} className="text-slate-400" />
          <span className="text-[13px] font-semibold text-slate-900">Réglages par salarié</span>
          <span className="text-[11.5px] text-slate-500">
            repos fixes, semaines A/B, semaine type complète
          </span>
          {showSettings
            ? <ChevronDown size={16} className="text-slate-400 ml-auto" />
            : <ChevronRight size={16} className="text-slate-400 ml-auto" />}
        </button>

        {showSettings && (
          <div className="border-t border-slate-100 p-3 space-y-2 bg-slate-50/40">
            {drafts.map((d) => {
              const isOpen = expanded === d.employeeId;
              const worked = d.rows.filter((r) => r.works).length;
              return (
                <div key={d.employeeId} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
                  <button
                    onClick={() => setExpanded(isOpen ? null : d.employeeId)}
                    className="w-full flex items-center gap-3 p-3.5 hover:bg-slate-50 text-left"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[12px] font-semibold flex-shrink-0" style={{ backgroundColor: d.color }}>
                      {d.firstName[0]}{d.lastName[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-slate-900">{d.firstName} {d.lastName}</span>
                        {/* Seuls les salariés marqués « Modifié » seront écrits en base. */}
                        {modifiedIds.has(d.employeeId) && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-bg text-primary border border-primary/25">
                            Modifié
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {POSITION_LABELS[d.position]} · {d.scheduleMode === 'ALTERNATING' ? 'Semaines A/B' : 'Semaine fixe'} · {worked} demi-jour{worked > 1 ? 's' : ''} travaillé{worked > 1 ? 's' : ''}
                      </div>
                    </div>
                    {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50/40">
                      <div className="grid grid-cols-2 gap-3">
                        <FixedOffSelect
                          label="Repos fixe 1"
                          value={d.fixedDayOff}
                          onChange={(v) => updateDraft(d.employeeId, { fixedDayOff: v })}
                        />
                        <FixedOffSelect
                          label="Repos fixe 2"
                          value={d.fixedDayOff2}
                          onChange={(v) => updateDraft(d.employeeId, { fixedDayOff2: v })}
                        />
                      </div>

                      <WeeklyTemplateEditor
                        employeeId={d.employeeId}
                        draftSeed={{ scheduleMode: d.scheduleMode, rows: d.rows }}
                        draftOpeningHours={pharmacy?.openingHours}
                        onDraftChange={(m: ScheduleMode, rows: WeeklyTemplateDay[]) =>
                          updateDraft(d.employeeId, { scheduleMode: m, rows })
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modale d'application */}
      {showApply && (
        <ApplyModal
          mode={mode}
          startDate={startDate}
          endDate={endDate}
          keepExceptions={keepExceptions}
          setKeepExceptions={setKeepExceptions}
          onClose={() => setShowApply(false)}
          onConfirm={handleApply}
          loading={applyMut.isPending}
          error={applyMut.error as Error | null}
          names={drafts.filter((d) => modifiedIds.has(d.employeeId)).map((d) => `${d.firstName} ${d.lastName}`)}
        />
      )}

      {applyMut.isSuccess && !showApply && (
        <div className="fixed bottom-24 right-6 z-40 max-w-sm rounded-lg shadow-lg overflow-hidden">
          <div className="bg-primary text-white px-4 py-3 text-[13px] flex items-start gap-2">
            <span className="flex-1">
              {mode === 'temporary'
                ? `✓ Changements appliqués du ${format(new Date(startDate), DATE_FMT)} au ${endDate ? format(new Date(endDate), DATE_FMT) : ''} (retour automatique ensuite).`
                : `✓ Changements appliqués à partir du ${format(new Date(startDate), DATE_FMT)}.`}
            </span>
            <button onClick={() => applyMut.reset()} className="p-0.5 rounded hover:bg-white/20 flex-shrink-0">
              <X size={13} />
            </button>
          </div>

          {/* Ce que l'application n'a PAS pu faire doit se voir. « Ça en a fait
              90 %, mais pas tout » venait de jours verrouillés par une semaine
              déjà validée : le serveur les sautait, l'écran n'en disait rien. */}
          {!!applyMut.data?.lockedDays && (
            <div className="bg-amber-50 border-t border-amber-200 px-4 py-2.5 text-[11.5px] text-amber-900 flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
              <span>
                <strong>{applyMut.data.lockedDays} jour{applyMut.data.lockedDays > 1 ? 's' : ''}</strong> n'{applyMut.data.lockedDays > 1 ? 'ont' : 'a'} pas
                été modifié{applyMut.data.lockedDays > 1 ? 's' : ''} : la semaine est déjà validée.
                Déverrouillez-la depuis « Validation » pour l'y appliquer aussi.
              </span>
            </div>
          )}
          {!!applyMut.data?.clearedManual && (
            <div className="bg-slate-50 border-t border-slate-200 px-4 py-2 text-[11px] text-slate-600">
              {applyMut.data.clearedManual} jour{applyMut.data.clearedManual > 1 ? 's' : ''} saisi
              {applyMut.data.clearedManual > 1 ? 's' : ''} à la main {applyMut.data.clearedManual > 1 ? 'ont' : 'a'} été
              remplacé{applyMut.data.clearedManual > 1 ? 's' : ''} par les nouveaux horaires.
            </div>
          )}
        </div>
      )}
    </div>

    {/* Barre d'action collée en bas : « on fige et on applique ». Elle ne
        s'affiche que s'il y a quelque chose à figer. */}
    {modifiedIds.size > 0 && (
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur px-6 py-3 flex items-center gap-3 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
        <span className="text-[12.5px] text-slate-700">
          <strong>{modifiedIds.size}</strong> salarié{modifiedIds.size > 1 ? 's' : ''} modifié{modifiedIds.size > 1 ? 's' : ''}
          <span className="text-slate-400"> — rien n'est enregistré pour l'instant</span>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="default" icon={Undo2} onClick={resetAll}>Tout annuler</Button>
          <Button variant="primary" icon={Rocket} onClick={() => setShowApply(true)}>
            Appliquer ces changements
          </Button>
        </div>
      </div>
    )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'aperçu : des semaines empilées, au visuel du planning
// ---------------------------------------------------------------------------

interface WeekBlock {
  key: string;
  iso: number;
  monday: Date;
  /** Les jours de l'aperçu présents dans cette semaine, indexés par jour affiché. */
  byPosition: (SimPreview['days'][number] | undefined)[];
}

interface OpenEditor {
  employeeId: string;
  dateIso: string;
  weekType: TemplateWeekType;
  dayOfWeek: number;
  siteId: string;
  rect: DOMRect;
}

function PreviewBoards({
  preview, baseline, drafts, sites, sitesMeta, scope, startDate,
  fullscreen, onToggleFullscreen, onEditDay, onClearFixedOff, refreshing,
}: {
  preview: SimPreview;
  baseline: SimPreview | null;
  drafts: SimDraft[];
  sites: { id: string; name: string; city: string | null }[];
  sitesMeta: { id: string; name: string; city: string | null; opensOnSunday?: boolean }[];
  scope: SimScope;
  startDate: string;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  onEditDay: (
    employeeId: string, weekType: TemplateWeekType, dayOfWeek: number,
    patch: Partial<WeeklyTemplateDay> | null,
  ) => void;
  /** Lève le repos fixe hebdomadaire d'un salarié sur ce jour de la semaine. */
  onClearFixedOff: (employeeId: string, weekday: number) => void;
  refreshing: boolean;
}) {
  const [editor, setEditor] = useState<OpenEditor | null>(null);
  const [onlyChanges, setOnlyChanges] = useState(false);

  // Le dimanche n'apparaît que si au moins une officine servie ouvre ce jour-là
  // — même convention que le planning et la vue multi-site.
  const dayCount = useMemo(() => {
    const served = new Set(sites.map((s) => s.id));
    return sitesMeta.some((s) => served.has(s.id) && s.opensOnSunday) ? 7 : 6;
  }, [sites, sitesMeta]);

  // Échap referme l'éditeur, puis le plein écran.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editor) setEditor(null);
      else if (fullscreen) onToggleFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editor, fullscreen, onToggleFullscreen]);

  const draftByEmp = useMemo(
    () => new Map(drafts.map((d) => [d.employeeId, d])),
    [drafts],
  );

  // Ce que la modification change, case par case — c'est tout l'intérêt d'une
  // simulation : sans ça il faudrait comparer de tête avec le planning réel.
  const diff = useMemo(() => {
    if (!baseline) return null;
    const baseByDate = new Map(baseline.days.map((d) => [d.date, d.cells]));
    const changedCells = new Set<string>();
    const changedEmployees = new Set<string>();
    for (const day of preview.days) {
      const before = baseByDate.get(day.date);
      if (!before) continue;
      for (const empId of Object.keys(day.cells)) {
        if (!sameCell(before[empId], day.cells[empId])) {
          changedCells.add(`${empId}|${day.date}`);
          changedEmployees.add(empId);
        }
      }
    }
    return { changedCells, changedEmployees, baseByDate };
  }, [preview, baseline]);

  // Découpage en semaines ISO, chaque jour placé dans sa colonne (lundi → dim).
  const weeks = useMemo<WeekBlock[]>(() => {
    const map = new Map<string, WeekBlock>();
    for (const day of preview.days) {
      const d = parseISO(day.date.slice(0, 10));
      const monday = startOfISOWeek(d);
      const key = format(monday, 'yyyy-MM-dd');
      let block = map.get(key);
      if (!block) {
        block = { key, iso: getISOWeek(monday), monday, byPosition: Array(7).fill(undefined) };
        map.set(key, block);
      }
      const pos = WEEK_DAY_NUMBERS.indexOf(d.getDay());
      if (pos >= 0) block.byPosition[pos] = day;
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [preview]);

  const employeesById = useMemo(
    () => new Map(preview.employees.map((e) => [e.id, e])),
    [preview],
  );

  const rowsFor = useCallback(
    (siteId: string | null, week: WeekBlock) => {
      const list = preview.employees.filter((emp) => {
        if (onlyChanges && diff && !diff.changedEmployees.has(emp.id)) return false;
        // Hors contrat toute la semaine : pas de ligne. Une ligne de tirets
        // pour quelqu'un qui n'est pas encore arrivé se lit comme un salarié
        // qui ne travaille jamais.
        const days = week.byPosition.filter((d) => !!d);
        if (days.length > 0 && days.every((d) => d!.cells[emp.id]?.status === 'NOT_EMPLOYED')) {
          return false;
        }
        if (!siteId) return true;
        // En multi-site, un salarié figure sous une officine s'il y a une
        // journée à lui cette semaine-là… ou simplement s'il y travaille (au
        // sens « officines du salarié »). Sans ce second cas, une officine où
        // il n'a encore AUCUN jour n'affichait pas sa ligne — et il était donc
        // impossible de lui en créer un depuis là : il fallait passer par son
        // officine de rattachement, puis déplacer la journée. C'est exactement
        // la manip en trois temps que le boss a dû trouver tout seul.
        if (
          week.byPosition.some((day) => {
            const c = day?.cells[emp.id];
            return !!c && (c.siteId === siteId || c.afternoonSiteId === siteId);
          })
        ) return true;
        return draftByEmp.get(emp.id)?.siteIds.includes(siteId) ?? false;
      });
      return [...list].sort(byPositionThenName);
    },
    [preview, onlyChanges, diff, draftByEmp],
  );

  const changedCount = diff?.changedCells.size ?? 0;

  return (
    <div
      className={
        fullscreen
          ? 'flex flex-col h-full'
          : 'border border-slate-200 rounded-xl bg-white overflow-hidden'
      }
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap flex-shrink-0">
        <CalendarDays size={14} className="text-primary" />
        <div className="text-[13px] font-semibold text-slate-900">Emploi du temps prévu</div>
        <div className="text-[11px] text-slate-400 tabular-nums">
          du {format(parseISO(preview.from.slice(0, 10)), DATE_FMT)} au{' '}
          {format(parseISO(preview.to.slice(0, 10)), DATE_FMT)}
        </div>

        {diff && (
          <span className={`text-[11.5px] font-medium px-2 py-0.5 rounded-full ${
            changedCount > 0
              ? 'bg-amber-50 text-amber-800 border border-amber-200'
              : 'bg-slate-100 text-slate-500'
          }`}>
            {changedCount === 0
              ? 'Aucun jour modifié'
              : `${changedCount} jour${changedCount > 1 ? 's' : ''} modifié${changedCount > 1 ? 's' : ''} · ${diff.changedEmployees.size} salarié${diff.changedEmployees.size > 1 ? 's' : ''}`}
          </span>
        )}

        {refreshing && (
          <span className="flex items-center gap-1 text-[11px] text-slate-400">
            <RefreshCw size={11} className="animate-spin" /> recalcul…
          </span>
        )}

        <div className="ml-auto flex items-center gap-3">
          {diff && diff.changedEmployees.size > 0 && (
            <label className="flex items-center gap-1.5 text-[11.5px] text-slate-600 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} className="rounded" />
              Uniquement les salariés modifiés
            </label>
          )}
          <button
            type="button"
            onClick={onToggleFullscreen}
            title={fullscreen ? 'Quitter le plein écran (Échap)' : 'Afficher en plein écran'}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-[11.5px] font-medium text-slate-600 hover:bg-slate-50"
          >
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {fullscreen ? 'Quitter' : 'Plein écran'}
          </button>
        </div>
      </div>

      <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 text-[11px] text-slate-500 flex-shrink-0">
        <Info size={12} className="text-slate-400 flex-shrink-0" />
        Cliquez sur une case pour changer ses horaires. La modification porte sur la semaine type :
        elle vaut pour tous les jours de la semaine concernés.
      </div>

      <div className={fullscreen ? 'flex-1 overflow-auto p-4 space-y-5' : 'p-4 space-y-5 max-h-[70vh] overflow-auto'}>
        {weeks.map((week) => (
          <div key={week.key} className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
              <span className="text-[12px] font-semibold text-slate-800">
                Semaine {week.iso}
              </span>
              <span className="text-[11px] text-slate-500 tabular-nums">
                {format(week.monday, 'd MMM', { locale: fr })} → {format(addDays(week.monday, dayCount - 1), 'd MMM yyyy', { locale: fr })}
              </span>
            </div>

            <div className="p-3 space-y-4">
              {scope === 'group' && sites.length > 1
                ? sites.map((site) => {
                    const rows = rowsFor(site.id, week);
                    return (
                      <div key={site.id}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Building2 size={13} className="text-slate-400 flex-shrink-0" />
                          <span className="text-[12.5px] font-semibold text-slate-900">{site.name}</span>
                          {site.city && <span className="text-[10.5px] text-slate-400">{site.city}</span>}
                        </div>
                        {rows.length === 0 ? (
                          <div className="text-[11.5px] text-slate-400 italic py-2">
                            Personne dans cette officine cette semaine.
                          </div>
                        ) : (
                          <WeekGrid
                            week={week}
                            dayCount={dayCount}
                            rows={rows}
                            siteId={site.id}
                            sites={sites}
                            diff={diff}
                            draftByEmp={draftByEmp}
                            startDate={startDate}
                            onOpen={setEditor}
                          />
                        )}
                      </div>
                    );
                  })
                : (() => {
                    const rows = rowsFor(null, week);
                    return rows.length === 0 ? (
                      <div className="text-[11.5px] text-slate-400 italic py-2">Aucun salarié.</div>
                    ) : (
                      <WeekGrid
                        week={week}
                        dayCount={dayCount}
                        rows={rows}
                        siteId={null}
                        sites={sites}
                        diff={diff}
                        draftByEmp={draftByEmp}
                        startDate={startDate}
                        onOpen={setEditor}
                      />
                    );
                  })()}
            </div>
          </div>
        ))}
      </div>

      {editor && (
        <DayEditor
          editor={editor}
          draft={draftByEmp.get(editor.employeeId)}
          employee={employeesById.get(editor.employeeId)}
          sites={sites}
          onClose={() => setEditor(null)}
          onEditDay={onEditDay}
          onClearFixedOff={onClearFixedOff}
        />
      )}
    </div>
  );
}

/** Une semaine, une officine : la grille elle-même. */
function WeekGrid({
  week, dayCount, rows, siteId, sites, diff, draftByEmp, startDate, onOpen,
}: {
  week: WeekBlock;
  dayCount: number;
  rows: SimPreview['employees'];
  siteId: string | null;
  sites: { id: string; name: string; city: string | null }[];
  diff: { changedCells: Set<string>; changedEmployees: Set<string>; baseByDate: Map<string, Record<string, SimCell>> } | null;
  draftByEmp: Map<string, SimDraft>;
  startDate: string;
  onOpen: (e: OpenEditor) => void;
}) {
  const COLS = `150px repeat(${dayCount}, minmax(118px, 1fr)) 58px`;
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? 'autre officine';

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: 208 + dayCount * 128 }}>
        <div
          className="grid text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-1.5"
          style={{ gridTemplateColumns: COLS }}
        >
          <div />
          {WEEK_DAY_LABELS.slice(0, dayCount).map((label, i) => (
            <div key={label} className="text-center">
              {label.slice(0, 3)}{' '}
              <span className="text-slate-400 normal-case tracking-normal">
                {format(addDays(week.monday, i), 'd/MM')}
              </span>
            </div>
          ))}
          <div className="text-right">Total</div>
        </div>

        {rows.map((emp) => {
          const st = positionStyle(emp.position);
          const draft = draftByEmp.get(emp.id);
          let total = 0;

          const cells = Array.from({ length: dayCount }).map((_, i) => {
            const day = week.byPosition[i];
            const date = addDays(week.monday, i);
            const dateIso = format(date, 'yyyy-MM-dd');

            // Jour hors de la fenêtre d'aperçu (avant la date d'effet, ou après
            // les quatre semaines). Rien à montrer, rien à éditer.
            if (!day) {
              const before = dateIso < startDate.slice(0, 10);
              return (
                <div key={i} className="px-0.5">
                  <div
                    title={before ? "Avant la date d'effet du changement" : 'Hors de la période affichée'}
                    className="rounded border border-dashed border-slate-200 bg-slate-50/50 text-[9.5px] text-center py-2.5 text-slate-300"
                  >
                    ·
                  </div>
                </div>
              );
            }

            const c = day.cells[emp.id];
            const changed = diff?.changedCells.has(`${emp.id}|${day.date}`) ?? false;
            // Un jour de REPOS n'appartient à aucune officine : il est éditable
            // depuis n'importe laquelle de celles du salarié. Avant, le repos
            // suivait la pharmacie de rattachement, donc « le faire travailler »
            // n'était proposé que là-bas — d'où le blocage sur le lundi de Zine
            // au Palais. Un jour TRAVAILLÉ, lui, reste la propriété de son site.
            const isRest = c?.status === 'OFF';
            // Une journée peut se partager entre DEUX officines : matin ici,
            // après-midi là-bas. Chaque moitié se regarde donc séparément, et
            // le total de la ligne ne compte que ce qui se fait ici.
            const morningHere = !siteId || c?.siteId === siteId || isRest;
            const afternoonHere = !siteId || c?.afternoonSiteId === siteId || isRest;
            const belongsHere = morningHere || afternoonHere;
            if (belongsHere) {
              total += cellHours(c, { morning: morningHere, afternoon: afternoonHere });
            }

            // La journée ENTIÈRE se fait ailleurs : on le dit, mais on n'y
            // touche pas depuis ce bloc — c'est celui de l'autre site qui la
            // porte. (Une journée à moitié ici reste éditable : voir plus bas.)
            if (!belongsHere) {
              return (
                <div key={i} className="px-0.5">
                  <div
                    title={`Cette journée se fait à ${siteName(c!.siteId)}`}
                    className="rounded border border-slate-100 bg-slate-50 text-[9px] text-center py-2.5 text-slate-400 truncate px-1"
                  >
                    → {siteName(c!.siteId)}
                  </div>
                </div>
              );
            }

            // Hors contrat : pas encore embauché, ou déjà parti.
            if (c?.status === 'NOT_EMPLOYED') {
              return (
                <div key={i} className="px-0.5">
                  <div
                    title="Hors période du contrat"
                    className="rounded border border-slate-100 bg-slate-50/60 text-[9.5px] text-center py-2.5 text-slate-300"
                  >
                    —
                  </div>
                </div>
              );
            }

            // Absence : case effacée, le motif ne subsiste que dans la pastille
            // — exactement comme dans l'écran Planning.
            if (c && c.status !== 'WORK' && c.status !== 'OFF') {
              const m = c.absenceType ? absenceMeta(c.absenceType as never) : null;
              return (
                <div key={i} className="px-0.5">
                  <div
                    title={m ? m.label : 'Absence'}
                    className={`rounded border flex items-center justify-center gap-1 text-[9.5px] py-2.5 ${ABSENCE_CELL_MUTED}`}
                  >
                    {m && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.dot }} />}
                    {m ? m.short : 'Absence'}
                  </div>
                </div>
              );
            }

            const weekType = weekTypeOf(draft?.scheduleMode ?? 'FIXED', date);
            const open = (ev: React.MouseEvent<HTMLElement>) =>
              onOpen({
                employeeId: emp.id,
                dateIso,
                weekType,
                dayOfWeek: date.getDay(),
                // L'officine du BLOC d'abord : cliquer une case sous « Pharmacie
                // du Palais », c'est parler du Palais. C'est ce qui permet de
                // faire travailler quelqu'un ici sans repasser par ailleurs.
                siteId: siteId ?? c?.siteId ?? emp.homeSiteId,
                rect: ev.currentTarget.getBoundingClientRect(),
              });

            const morning = c?.status === 'WORK' && !!c.morningStart;
            const afternoon = c?.status === 'WORK' && !!c.afternoonStart;

            // Chaque moitié : travaillée ici, travaillée ailleurs, ou vide.
            const halves = [
              {
                slot: 'morning' as const,
                on: morning && morningHere,
                elsewhere: morning && !morningHere ? siteName(c!.siteId) : null,
                text: morning ? `${c!.morningStart}-${c!.morningEnd}` : '—',
              },
              {
                slot: 'afternoon' as const,
                on: afternoon && afternoonHere,
                elsewhere: afternoon && !afternoonHere ? siteName(c!.afternoonSiteId) : null,
                text: afternoon ? `${c!.afternoonStart}-${c!.afternoonEnd}` : '—',
              },
            ];

            return (
              <div key={i} className="px-0.5">
                <button
                  type="button"
                  onClick={open}
                  title={`${cellTooltip(c)} — cliquer pour modifier`}
                  className={`w-full grid grid-cols-2 gap-0.5 rounded p-[1px] text-left transition-shadow hover:ring-2 hover:ring-primary/40 ${
                    changed ? 'ring-2 ring-amber-400 bg-amber-50' : ''
                  }`}
                >
                  {halves.map((h) => (
                    <span
                      key={h.slot}
                      title={h.elsewhere ? `${h.text} à ${h.elsewhere}` : undefined}
                      style={h.on ? { backgroundColor: st.bg, color: st.text, borderColor: st.bg } : undefined}
                      className={`rounded border text-[9px] px-1 py-1.5 text-center tabular-nums block truncate ${
                        h.on
                          ? 'font-semibold'
                          : h.elsewhere
                          ? 'bg-slate-100 text-slate-400 border-slate-200 font-medium'
                          : 'bg-slate-50/60 text-slate-300 border-slate-200 font-medium'
                      }`}
                    >
                      {/* La demi-journée faite dans l'autre officine n'est pas
                          vide : elle est AILLEURS. Le dire évite de la lire
                          comme un repos — et rappelle où va le salarié. */}
                      {h.elsewhere ? `→ ${h.elsewhere}` : h.on ? h.text : '—'}
                    </span>
                  ))}
                </button>
              </div>
            );
          });

          return (
            <div
              key={emp.id}
              className="grid items-center border-b border-slate-100 py-1"
              style={{ gridTemplateColumns: COLS }}
            >
              <div className="flex items-center gap-1.5 min-w-0 pr-2">
                <span className="w-1 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: st.bg }} />
                <span className="text-[11.5px] truncate">{emp.firstName} {emp.lastName}</span>
              </div>
              {cells}
              <div className="text-right">
                <div className="text-[11px] font-semibold tabular-nums">
                  {total > 0 ? `${total.toFixed(1)}h` : '—'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'éditeur d'une journée — ouvert au clic sur une case
// ---------------------------------------------------------------------------

/**
 * Ce panneau modifie la LIGNE DE SEMAINE TYPE derrière la case cliquée, pas la
 * date. C'est écrit noir sur blanc en tête : sans ça, changer le mercredi 14/10
 * et voir bouger tous les mercredis passerait pour un bug.
 */
function DayEditor({
  editor, draft, employee, sites, onClose, onEditDay, onClearFixedOff,
}: {
  editor: OpenEditor;
  draft: SimDraft | undefined;
  employee: SimPreview['employees'][number] | undefined;
  sites: { id: string; name: string; city: string | null }[];
  onClose: () => void;
  onEditDay: (
    employeeId: string, weekType: TemplateWeekType, dayOfWeek: number,
    patch: Partial<WeeklyTemplateDay> | null,
  ) => void;
  onClearFixedOff: (employeeId: string, weekday: number) => void;
}) {
  // Horaires d'ouverture de l'officine qui tient la journée : ils servent à
  // pré-remplir une journée de repos qu'on décide de faire travailler.
  const { data: sitePharmacy } = useSitePharmacy(editor.siteId);

  const row = draft?.rows.find(
    (r) => r.weekType === editor.weekType && r.dayOfWeek === editor.dayOfWeek,
  );
  const works = !!row?.works && (!!row.morningStart || !!row.afternoonStart);
  // Le repos fixe est une contrainte du salarié, pas une ligne de semaine type :
  // il écrase le jour après coup, dans le moteur.
  const isFixedOff =
    draft?.fixedDayOff === editor.dayOfWeek || draft?.fixedDayOff2 === editor.dayOfWeek;
  const defaults = slotDefaults(sitePharmacy?.openingHours, editor.dayOfWeek);
  const closed = closedByWeekday(sitePharmacy?.openingHours).get(editor.dayOfWeek);

  const date = parseISO(editor.dateIso);
  const dayLabel = format(date, 'EEEE', { locale: fr });
  const isAlternating = draft?.scheduleMode === 'ALTERNATING';

  // Le panneau se pose sous la case, recalé pour rester dans la fenêtre.
  const width = 300;
  const left = Math.min(Math.max(8, editor.rect.left), window.innerWidth - width - 8);
  const below = editor.rect.bottom + 8;
  const top = below + 300 > window.innerHeight ? Math.max(8, editor.rect.top - 300 - 8) : below;

  const set = (patch: Partial<WeeklyTemplateDay>) =>
    onEditDay(editor.employeeId, editor.weekType, editor.dayOfWeek, patch);

  /** L'officine d'où le clic est parti — c'est elle qu'on affecte. */
  const clickedSite = sites.find((s) => s.id === editor.siteId);

  /**
   * Passer une journée de repos à « travaillée » : horaires de l'officine.
   *
   * L'officine est posée EN MÊME TEMPS. Sans ça, faire travailler quelqu'un
   * depuis le bloc « Pharmacie du Palais » créait bien la journée, mais sur sa
   * pharmacie de rattachement : elle n'apparaissait jamais là où on l'avait
   * demandée, et seule la manip en trois temps (rattachement → le faire
   * travailler → le déplacer → horaires) donnait le bon résultat.
   */
  const startWorking = () => {
    // Pharmacie fermée toute la journée : on poserait `works: true` sans une
    // seule heure, donc rien du tout — et le clic n'aurait aucun effet visible.
    // Certains postes travaillent justement hors ouverture (ménage, magasinier,
    // renfort du soir) : on propose alors les horaires par défaut, quitte à ce
    // que le planning les signale « hors ouverture ».
    const shutAllDay = !!closed?.morning && !!closed?.afternoon;
    set({
      works: true,
      pharmacyId: draft && editor.siteId === draft.homeSiteId ? null : editor.siteId,
      // On repart d'une journée entière dans cette officine ; le partage entre
      // deux officines se décide ensuite, demi-journée par demi-journée.
      afternoonPharmacyId: null,
      morningStart: closed?.morning && !shutAllDay ? null : defaults.morningStart,
      morningEnd: closed?.morning && !shutAllDay ? null : defaults.morningEnd,
      afternoonStart: closed?.afternoon && !shutAllDay ? null : defaults.afternoonStart,
      afternoonEnd: closed?.afternoon && !shutAllDay ? null : defaults.afternoonEnd,
    });
  };

  // ---------------------------------------------------------------------
  // Où se fait chaque moitié de la journée
  // ---------------------------------------------------------------------
  // « Il faut que je puisse choisir les horaires dans 2 pharmacies le même
  //   jour. […] Si c'est trop compliqué divise matin et aprem : matin possible
  //   de choisir où et aprem possible de choisir où. »
  //
  // C'est ce qui est fait, et les heures restant libres, l'exemple du boss
  // s'écrit tel quel : matin 09:00-16:00 à Saint-Pierre, après-midi 17:00-20:00
  // au Palais. Le matin porte l'adresse de la journée ; l'après-midi n'en a une
  // à lui que s'il diffère.
  const shared = !!draft && draft.siteIds.length > 1;
  const morningSite = row?.pharmacyId ?? draft?.homeSiteId ?? editor.siteId;
  const afternoonSite = row?.afternoonPharmacyId ?? morningSite;

  const setMorningSite = (siteId: string) =>
    set({
      pharmacyId: siteId === draft?.homeSiteId ? null : siteId,
      // L'après-midi ne suit pas le matin en douce : il garde l'officine qu'on
      // lisait à l'écran, et redevient implicite si c'est la même.
      afternoonPharmacyId: afternoonSite === siteId ? null : afternoonSite,
    });

  const setAfternoonSite = (siteId: string) =>
    set({ afternoonPharmacyId: siteId === morningSite ? null : siteId });

  /** Les officines du salarié, en boutons — une par demi-journée. */
  const siteRow = (current: string, onPick: (id: string) => void) => (
    <div className="flex flex-wrap gap-1 pl-[70px]">
      {sites
        .filter((s) => draft!.siteIds.includes(s.id))
        .map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(s.id)}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${
              current === s.id
                ? 'border-primary bg-primary-bg text-primary'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-400'
            }`}
          >
            {s.name}
          </button>
        ))}
    </div>
  );

  const half = (
    label: string,
    startKey: 'morningStart' | 'afternoonStart',
    endKey: 'morningEnd' | 'afternoonEnd',
    defStart: string,
    defEnd: string,
  ) => {
    const s = row?.[startKey] ?? null;
    const e = row?.[endKey] ?? null;
    const on = !!s && !!e;
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-slate-500 w-[70px] flex-shrink-0">{label}</span>
        {on ? (
          <>
            <input
              type="time"
              value={s ?? ''}
              onChange={(ev) => set({ works: true, [startKey]: ev.target.value } as Partial<WeeklyTemplateDay>)}
              className="input py-1 px-1 text-[11.5px] tabular-nums w-[92px]"
            />
            <span className="text-slate-300 text-[11px]">→</span>
            <input
              type="time"
              value={e ?? ''}
              onChange={(ev) => set({ works: true, [endKey]: ev.target.value } as Partial<WeeklyTemplateDay>)}
              className="input py-1 px-1 text-[11.5px] tabular-nums w-[92px]"
            />
            <button
              type="button"
              title="Retirer cette demi-journée"
              onClick={() => set({ [startKey]: null, [endKey]: null } as Partial<WeeklyTemplateDay>)}
              className="p-1 rounded text-slate-400 hover:text-danger hover:bg-danger-bg"
            >
              <X size={12} />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => set({ works: true, [startKey]: defStart, [endKey]: defEnd } as Partial<WeeklyTemplateDay>)}
            className="text-[11px] text-primary hover:underline"
          >
            + ajouter {label.toLowerCase()}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Cliquer à côté referme — pas de fond sombre : on veut continuer à voir
          la grille pendant qu'on règle une journée. */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 rounded-xl border border-slate-200 bg-white shadow-xl p-3 space-y-2.5"
        style={{ left, top, width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* `capitalize` mettrait aussi « Août » en majuscule : on ne relève
                que la première lettre du libellé. */}
            <div className="text-[12.5px] font-semibold text-slate-900 first-letter:uppercase truncate">
              {dayLabel} {format(date, 'd MMM', { locale: fr })}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {employee ? `${employee.firstName} ${employee.lastName}` : ''}
              {isAlternating && ` · semaine ${editor.weekType}`}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 flex-shrink-0">
            <X size={14} className="text-slate-500" />
          </button>
        </div>

        <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5 text-[10.5px] text-amber-800">
          <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
          <span>
            Modifie <strong>tous les {dayLabel.toLowerCase()}s</strong>
            {isAlternating && ` de semaine ${editor.weekType}`}, à partir de la date d'effet.
          </span>
        </div>

        {/* Le repos fixe hebdomadaire l'emporte sur la semaine type : tant qu'il
            est posé, les horaires saisis sur ce jour sont ignorés — sans que
            rien ne le dise. C'était l'une des raisons du « peu importe ce que
            je fais, ça ne change pas ». On le dit, et on le règle ici. */}
        {isFixedOff && (
          <div className="rounded-lg bg-danger-bg border border-danger/30 px-2 py-1.5 text-[10.5px] text-danger space-y-1">
            <div className="flex items-start gap-1.5">
              <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
              <span>
                <strong>Repos fixe hebdomadaire</strong> le {dayLabel}. Tant qu'il est posé, ce jour
                reste au repos <em>quels que soient</em> les horaires saisis ici.
              </span>
            </div>
            <button
              type="button"
              onClick={() => onClearFixedOff(editor.employeeId, editor.dayOfWeek)}
              className="font-semibold underline hover:no-underline"
            >
              Retirer ce repos fixe
            </button>
          </div>
        )}

        {works ? (
          <div className="space-y-1.5">
            {half('Matin', 'morningStart', 'morningEnd', defaults.morningStart, defaults.morningEnd)}
            {shared && !!row?.morningStart && siteRow(morningSite, setMorningSite)}
            {half('Après-midi', 'afternoonStart', 'afternoonEnd', defaults.afternoonStart, defaults.afternoonEnd)}
            {shared && !!row?.afternoonStart && siteRow(afternoonSite, setAfternoonSite)}
          </div>
        ) : (
          <div className="text-[11.5px] text-slate-500">
            Repos ce jour-là.{' '}
            <button type="button" onClick={startWorking} className="text-primary hover:underline font-medium">
              {clickedSite && draft && draft.siteIds.length > 1
                ? `Le faire travailler à ${clickedSite.name}`
                : 'Le faire travailler'}
            </button>
          </div>
        )}

        {/* Une journée partagée se lit d'un coup d'œil : c'est le seul cas où
            l'officine change en cours de journée, et il mérite d'être nommé. */}
        {shared && works && afternoonSite !== morningSite && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5 text-[10.5px] text-slate-600">
            Journée partagée : le matin à <strong>{sites.find((s) => s.id === morningSite)?.name}</strong>,
            l'après-midi à <strong>{sites.find((s) => s.id === afternoonSite)?.name}</strong>.
          </div>
        )}

        {works && (
          <button
            type="button"
            onClick={() => onEditDay(editor.employeeId, editor.weekType, editor.dayOfWeek, null)}
            className="w-full text-[11.5px] text-slate-500 hover:text-danger border border-slate-200 hover:border-danger/40 rounded-lg py-1.5"
          >
            Mettre au repos
          </button>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Réglages détaillés — inchangés
// ---------------------------------------------------------------------------

function FixedOffSelect({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number | null) => void }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {DAYS_OFF.map((d) => (
          <button
            key={String(d.v)}
            type="button"
            onClick={() => onChange(d.v)}
            className={`px-2 py-1 rounded-md text-[11px] font-medium border ${
              value === d.v ? 'border-primary bg-primary-bg text-primary' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-400'
            }`}
          >
            {d.l}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Champ de date en **jj/mm/aaaa**.
 *
 * `<input type="date">` affiche la date selon la langue du navigateur / de Windows
 * (mm/dd/yyyy en anglais) : ni le code, ni `lang`, ni le CSS ne peuvent l'imposer.
 * On saisit donc du texte au format français, et le calendrier natif reste
 * accessible par l'icône — via un `type="date"` masqué et `showPicker()`.
 */
function DateField({
  value, onChange, min,
}: {
  value: string;              // ISO yyyy-mm-dd
  onChange: (iso: string) => void;
  min?: string;
}) {
  const isoToFr = (iso: string) => {
    if (!iso || iso.length < 10) return '';
    const [y, m, d] = iso.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  };

  const [text, setText] = useState(() => isoToFr(value));
  const pickerRef = useRef<HTMLInputElement>(null);

  // Suit les changements venus d'ailleurs (ex. le calendrier natif).
  useEffect(() => { setText(isoToFr(value)); }, [value]);

  /**
   * « 12/08/2026 » → « 2026-08-12 », en refusant les dates inexistantes (31/02…).
   * Contrôle fait en UTC de bout en bout : `new Date('…T00:00:00')` est interprété en
   * heure LOCALE, donc comparer son `getUTCDate()` décale d'un jour hors UTC (GMT+1 ici)
   * et rejetait toutes les dates valides.
   */
  function parseFr(s: string): string | null {
    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const [, d, mo, y] = m;
    const day = Number(d), month = Number(mo), year = Number(y);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const dt = new Date(Date.UTC(year, month - 1, day));
    // Un débordement (31/02 → 03/03) change le mois : c'est le test de validité.
    if (dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null;
    return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  function handleText(next: string) {
    // Barres obliques ajoutées au fil de la frappe.
    const digits = next.replace(/\D/g, '').slice(0, 8);
    const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
    const formatted = next.endsWith('/') && digits.length <= 4 ? next : parts.join('/');
    setText(formatted);
    const iso = parseFr(formatted);
    if (iso) onChange(iso);
  }

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        placeholder="jj/mm/aaaa"
        value={text}
        onChange={(e) => handleText(e.target.value)}
        onBlur={() => setText(isoToFr(value))} // saisie incomplète → on revient à la valeur réelle
        className="input w-full tabular-nums pr-9"
      />
      <button
        type="button"
        title="Ouvrir le calendrier"
        onClick={() => {
          const el = pickerRef.current;
          if (!el) return;
          if (typeof el.showPicker === 'function') el.showPicker();
          else el.click(); // repli si showPicker() n'existe pas
        }}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
      >
        <CalendarDays size={14} />
      </button>
      <input
        ref={pickerRef}
        type="date"
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute right-2 bottom-0 w-0 h-0 opacity-0 pointer-events-none"
      />
    </div>
  );
}

/**
 * Nature du changement, posée en tête de page : permanent ou temporaire ne change
 * pas seulement l'enregistrement, ça change ce qu'on est en train de décider.
 * La poser dans la modale finale revenait à demander l'essentiel en dernier.
 */
function PlanBar({
  mode, setMode, startDate, setStartDate, endDate, setEndDate,
}: {
  mode: 'permanent' | 'temporary';
  setMode: (v: 'permanent' | 'temporary') => void;
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
}) {
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">
        Nature du changement
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          onClick={() => setMode('permanent')}
          className={`p-3 rounded-xl border text-left transition-all ${mode === 'permanent' ? 'border-primary bg-primary-bg/40 ring-2 ring-primary/10' : 'border-slate-200 bg-white hover:border-slate-400'}`}
        >
          <div className="text-[12.5px] font-semibold text-slate-900">Permanent</div>
          <div className="text-[10.5px] text-slate-500 mt-0.5">Nouveaux horaires habituels, à partir d'une date</div>
        </button>
        <button
          type="button"
          onClick={() => setMode('temporary')}
          className={`p-3 rounded-xl border text-left transition-all ${mode === 'temporary' ? 'border-primary bg-primary-bg/40 ring-2 ring-primary/10' : 'border-slate-200 bg-white hover:border-slate-400'}`}
        >
          <div className="text-[12.5px] font-semibold text-slate-900">Temporaire</div>
          <div className="text-[10.5px] text-slate-500 mt-0.5">Sur une période, puis retour tout seul à l'actuel</div>
        </button>
      </div>

      <div className={mode === 'temporary' ? 'grid grid-cols-2 gap-3' : 'max-w-xs'}>
        <div>
          <label className="text-[12px] font-medium text-slate-700 block mb-1">
            {mode === 'temporary' ? 'Du' : 'À partir du'}
          </label>
          <DateField value={startDate} onChange={setStartDate} />
        </div>
        {mode === 'temporary' && (
          <div>
            <label className="text-[12px] font-medium text-slate-700 block mb-1">Au</label>
            <DateField value={endDate} onChange={setEndDate} min={startDate} />
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-500 mt-2">
        {mode === 'permanent'
          ? 'La semaine type du salarié est mise à jour (visible dans « Horaires & disponibilités »). Le planning avant cette date reste inchangé.'
          : "Le planning suit le nouveau rythme sur la période, puis revient automatiquement à la semaine type actuelle. La semaine type de référence n'est pas modifiée."}
      </p>
    </div>
  );
}

function ApplyModal({
  mode, startDate, endDate,
  keepExceptions, setKeepExceptions, onClose, onConfirm, loading, error, names,
}: {
  mode: 'permanent' | 'temporary';
  startDate: string;
  endDate: string;
  keepExceptions: boolean;
  setKeepExceptions: (v: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  error: Error | null;
  names: string[];
}) {
  const canConfirm = names.length > 0 && (mode === 'permanent' || (!!endDate && endDate >= startDate));
  const d = (iso: string) => (iso ? format(new Date(iso), DATE_FMT) : '—');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[16px] font-semibold text-slate-900">Appliquer les changements</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X size={16} className="text-slate-500" /></button>
        </div>

        {/* Récapitulatif : le mode et les dates ont été choisis en haut de page. */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5">
          <div className="flex justify-between text-[12.5px]">
            <span className="text-slate-500">Nature</span>
            <span className="font-semibold text-slate-900">{mode === 'permanent' ? 'Permanent' : 'Temporaire'}</span>
          </div>
          <div className="flex justify-between text-[12.5px]">
            <span className="text-slate-500">{mode === 'temporary' ? 'Période' : 'À partir du'}</span>
            <span className="font-semibold text-slate-900 tabular-nums">
              {mode === 'temporary' ? `${d(startDate)} → ${d(endDate)}` : d(startDate)}
            </span>
          </div>
          <div className="flex justify-between gap-4 text-[12.5px]">
            <span className="text-slate-500 flex-shrink-0">Salariés concernés</span>
            <span className="font-semibold text-slate-900 text-right">
              {names.length > 0 ? names.join(', ') : 'aucun'}
            </span>
          </div>
        </div>

        <div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={keepExceptions} onChange={(e) => setKeepExceptions(e.target.checked)} className="mt-0.5 rounded" />
            <span className="text-[12.5px] text-slate-700">
              Conserver les événements exceptionnels déjà saisis (congés, absences…)
            </span>
          </label>
          {!keepExceptions && mode === 'permanent' && (
            <div className="mt-2 flex items-start gap-2 bg-danger-bg border border-danger/30 rounded-lg p-2.5 text-[11.5px] text-danger">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
              Les congés et absences approuvés à partir de cette date seront <strong>supprimés</strong>.
            </div>
          )}
        </div>

        {/* Dit avant, pas après : un jour déjà retouché à la main sur le planning
            repasse sous les nouveaux horaires. C'est ce qu'on veut quand on
            applique un changement permanent — mais ça doit être annoncé. */}
        {mode === 'permanent' && (
          <p className="text-[11px] text-slate-500">
            Les jours déjà retouchés à la main sur le planning, à partir de cette date, repassent
            sous les nouveaux horaires. Les semaines déjà validées, elles, restent verrouillées.
          </p>
        )}

        {error && <div className="bg-danger-bg border border-danger/30 rounded-lg p-2.5 text-[12px] text-danger">{error.message}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="default" onClick={onClose}>Annuler</Button>
          <Button variant="primary" icon={Rocket} loading={loading} disabled={!canConfirm} onClick={onConfirm}>Appliquer</Button>
        </div>
      </div>
    </div>
  );
}
